<template>
  <v-container fluid class="pt-0">
    <v-card v-if="loading" class="text-center pa-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-4">Loading organization...</p>
    </v-card>

    <v-alert v-if="error" type="error" prominent>
      Could not load organization data. Please try again later.
    </v-alert>

    <v-card v-if="org && !loading && !error">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-3">fas fa-building</v-icon>
        <span>{{ org.display_name }}</span>
        <v-spacer></v-spacer>
        <div class="d-flex align-center ga-2">
          <v-chip :color="org.account_status === 'active' ? 'green' : 'red'" dark>
            {{ org.account_status }}
          </v-chip>
          <v-btn icon @click="closeView">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-divider></v-divider>

      <v-list dense>
        <v-list-item>
          <v-list-item-title>Slug</v-list-item-title>
          <v-list-item-subtitle>{{ org.name }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item>
          <v-list-item-title>Auth0 Org ID</v-list-item-title>
          <v-list-item-subtitle>{{ org.auth0_org_id }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="org.tier_id">
          <v-list-item-title>Tier ID</v-list-item-title>
          <v-list-item-subtitle>{{ org.tier_id }}</v-list-item-subtitle>
        </v-list-item>
      </v-list>

      <v-divider></v-divider>

      <!-- Members Section -->
      <v-card-title class="d-flex align-center pa-4">
        Members
        <v-spacer></v-spacer>
        <v-btn color="primary" size="small" @click="showInviteModal = true">
          <v-icon class="mr-1">fas fa-user-plus</v-icon>
          Invite
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="memberHeaders"
        :items="members"
        :loading="membersLoading"
        class="elevation-0"
        item-key="user_auth0_id"
        hide-default-footer
      >
        <template v-slot:[`item.joined_on`]="{ item }">
          {{ formatDate(item.joined_on) }}
        </template>
        <template v-slot:[`item.roles`]="{ item }">
          <v-chip
            v-for="role in item.roles"
            :key="role"
            size="small"
            class="mr-1"
            :color="role === 'org-owner' ? 'orange' : role === 'org-admin' ? 'blue' : 'grey'"
            dark
          >{{ role }}</v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            variant="text"
            density="compact"
            @click="openUpdateRoles(item)"
            title="Update Roles"
            :disabled="item.roles.includes('org-owner')"
          >
            <v-icon size="14">fas fa-user-tag</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            density="compact"
            color="error"
            @click="confirmRemoveMember(item)"
            title="Remove Member"
            :disabled="item.roles.includes('org-owner')"
          >
            <v-icon size="14">fas fa-user-minus</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            density="compact"
            color="warning"
            @click="openTransferOwnership(item)"
            title="Transfer Ownership"
            v-if="!item.roles.includes('org-owner')"
          >
            <v-icon size="14">fas fa-crown</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <!-- Pending Invitations Section -->
      <v-divider class="mt-2"></v-divider>
      <v-card-title class="pa-4">Pending Invitations</v-card-title>
      <v-list v-if="invitations.length > 0">
        <v-list-item
          v-for="inv in invitations"
          :key="inv.id"
          :subtitle="`Expires: ${formatDate(inv.expires_at)}`"
        >
          <template v-slot:title>{{ inv.invitee?.email }}</template>
          <template v-slot:append>
            <v-btn icon size="small" color="error" @click="revokeInvitation(inv.id)" :loading="revokingId === inv.id">
              <v-icon size="small">fas fa-times</v-icon>
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
      <v-card-text v-else class="text-grey">No pending invitations.</v-card-text>
    </v-card>

    <!-- Invite Modal -->
    <v-dialog v-model="showInviteModal" max-width="500">
      <v-card>
        <v-card-title>Invite Member</v-card-title>
        <v-card-text>
          <v-text-field v-model="inviteEmail" label="Email" type="email" required></v-text-field>
          <v-select
            v-model="inviteRoles"
            :items="['org-admin', 'org-developer']"
            label="Roles"
            multiple
            chips
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showInviteModal = false">Cancel</v-btn>
          <v-btn color="primary" @click="sendInvitation" :loading="inviteLoading" :disabled="!inviteEmail || inviteRoles.length === 0">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Update Roles Modal -->
    <v-dialog v-model="showUpdateRolesModal" max-width="500">
      <v-card>
        <v-card-title>Update Roles</v-card-title>
        <v-card-text>
          <p class="text-caption mb-2">{{ selectedMember?.name || selectedMember?.user_auth0_id }}</p>
          <v-select
            v-model="updatedRoles"
            :items="['org-admin', 'org-developer']"
            label="Roles"
            multiple
            chips
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showUpdateRolesModal = false">Cancel</v-btn>
          <v-btn color="primary" @click="submitUpdateRoles" :loading="rolesLoading">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Member Confirm -->
    <v-dialog v-model="showRemoveModal" max-width="450">
      <v-card>
        <v-card-title>Remove Member</v-card-title>
        <v-card-text>
          Remove <strong>{{ selectedMember?.name || selectedMember?.user_auth0_id }}</strong> from this organization?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showRemoveModal = false">Cancel</v-btn>
          <v-btn color="error" @click="submitRemoveMember" :loading="removeLoading">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Transfer Ownership Confirm -->
    <v-dialog v-model="showTransferModal" max-width="450">
      <v-card>
        <v-card-title>Transfer Ownership</v-card-title>
        <v-card-text>
          Transfer organization ownership to
          <strong>{{ selectedMember?.name || selectedMember?.user_auth0_id }}</strong>?
          The current owner will become an admin.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showTransferModal = false">Cancel</v-btn>
          <v-btn color="warning" @click="submitTransferOwnership" :loading="transferLoading">Transfer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';

export default {
  name: 'IndividualOrganization',
  setup() {
    const { getAccessTokenSilently } = useAuth0();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const org = ref(null);
    const members = ref([]);
    const invitations = ref([]);
    const loading = ref(true);
    const membersLoading = ref(false);
    const error = ref(false);

    const showInviteModal = ref(false);
    const showUpdateRolesModal = ref(false);
    const showRemoveModal = ref(false);
    const showTransferModal = ref(false);

    const selectedMember = ref(null);
    const inviteEmail = ref('');
    const inviteRoles = ref([]);
    const updatedRoles = ref([]);

    const inviteLoading = ref(false);
    const rolesLoading = ref(false);
    const removeLoading = ref(false);
    const transferLoading = ref(false);
    const revokingId = ref(null);

    const memberHeaders = [
      { title: 'Name', key: 'name', sortable: true },
      { title: 'Email', key: 'email' },
      { title: 'Roles', key: 'roles' },
      { title: 'Joined', key: 'joined_on' },
      { title: 'Actions', key: 'actions', sortable: false },
    ];

    const orgId = () => route.params.id;

    const baseUrl = (id = orgId()) => `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/organizations/${id}`;

    const authHeader = async () => {
      const token = await getAccessTokenSilently();
      return { Authorization: `Bearer ${token}` };
    };

    const handleError = (e, fallback) => {
      store.commit('SET_ERROR_MESSAGE', e.response?.data?.detail || e.message || fallback);
      store.commit('SET_SHOW_ERROR_MODAL', true);
    };

    const fetchOrg = async () => {
      const fetchId = orgId();
      const headers = await authHeader();
      const r = await axios.get(baseUrl(fetchId), { headers });
      if (orgId() === fetchId) org.value = r.data;
    };

    const fetchMembers = async () => {
      const fetchId = orgId();
      membersLoading.value = true;
      try {
        const headers = await authHeader();
        const r = await axios.get(`${baseUrl(fetchId)}/members`, { headers });
        if (orgId() === fetchId) members.value = r.data;
      } catch (e) {
        if (orgId() === fetchId) handleError(e, 'Failed to load members');
      } finally {
        if (orgId() === fetchId) membersLoading.value = false;
      }
    };

    const fetchInvitations = async () => {
      const fetchId = orgId();
      try {
        const headers = await authHeader();
        const r = await axios.get(`${baseUrl(fetchId)}/invitations`, { headers });
        if (orgId() === fetchId) invitations.value = r.data;
      } catch {
        if (orgId() === fetchId) invitations.value = [];
      }
    };

    const closeView = () => {
      router.push({ name: 'AllOrganizations' });
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString(undefined, { timeZone: 'UTC' });
    };

    const sendInvitation = async () => {
      const targetId = orgId();
      inviteLoading.value = true;
      try {
        const headers = await authHeader();
        await axios.post(
          `${baseUrl(targetId)}/invitations`,
          { email: inviteEmail.value, roles: inviteRoles.value },
          { headers }
        );
        showInviteModal.value = false;
        inviteEmail.value = '';
        inviteRoles.value = [];
        await fetchInvitations();
      } catch (e) {
        handleError(e, 'Failed to send invitation');
      } finally {
        inviteLoading.value = false;
      }
    };

    const revokeInvitation = async (invitationId) => {
      const targetId = orgId();
      revokingId.value = invitationId;
      try {
        const headers = await authHeader();
        await axios.delete(`${baseUrl(targetId)}/invitations/${invitationId}`, { headers });
        await fetchInvitations();
      } catch (e) {
        handleError(e, 'Failed to revoke invitation');
      } finally {
        revokingId.value = null;
      }
    };

    const openUpdateRoles = (member) => {
      selectedMember.value = member;
      updatedRoles.value = [...(member.roles || [])].filter((r) => r !== 'org-owner');
      showUpdateRolesModal.value = true;
    };

    const submitUpdateRoles = async () => {
      if (!selectedMember.value) return;
      const targetId = orgId();
      rolesLoading.value = true;
      try {
        const headers = await authHeader();
        await axios.put(
          `${baseUrl(targetId)}/members/${selectedMember.value.user_auth0_id}/roles`,
          { roles: updatedRoles.value },
          { headers }
        );
        showUpdateRolesModal.value = false;
        await fetchMembers();
      } catch (e) {
        handleError(e, 'Failed to update roles');
      } finally {
        rolesLoading.value = false;
      }
    };

    const confirmRemoveMember = (member) => {
      selectedMember.value = member;
      showRemoveModal.value = true;
    };

    const submitRemoveMember = async () => {
      if (!selectedMember.value) return;
      const targetId = orgId();
      removeLoading.value = true;
      try {
        const headers = await authHeader();
        await axios.delete(`${baseUrl(targetId)}/members/${selectedMember.value.user_auth0_id}`, { headers });
        showRemoveModal.value = false;
        await fetchMembers();
      } catch (e) {
        handleError(e, 'Failed to remove member');
      } finally {
        removeLoading.value = false;
      }
    };

    const openTransferOwnership = (member) => {
      selectedMember.value = member;
      showTransferModal.value = true;
    };

    const submitTransferOwnership = async () => {
      if (!selectedMember.value) return;
      const targetId = orgId();
      transferLoading.value = true;
      try {
        const headers = await authHeader();
        await axios.post(
          `${baseUrl(targetId)}/transfer-ownership`,
          { new_owner_auth0_id: selectedMember.value.user_auth0_id },
          { headers }
        );
        showTransferModal.value = false;
        await Promise.all([fetchOrg(), fetchMembers()]);
      } catch (e) {
        handleError(e, 'Failed to transfer ownership');
      } finally {
        transferLoading.value = false;
      }
    };

    watch(
      () => route.params.id,
      (newId, oldId) => {
        if (newId && newId !== oldId) {
          loading.value = true;
          error.value = false;
          Promise.all([fetchOrg(), fetchMembers(), fetchInvitations()])
            .catch((e) => {
              error.value = true;
              handleError(e, 'Failed to load organization');
            })
            .finally(() => {
              loading.value = false;
            });
        }
      }
    );

    watch(showInviteModal, (isOpen) => {
      if (!isOpen) {
        inviteEmail.value = '';
        inviteRoles.value = [];
      }
    });

    onMounted(async () => {
      loading.value = true;
      try {
        await Promise.all([fetchOrg(), fetchMembers(), fetchInvitations()]);
      } catch (e) {
        error.value = true;
        handleError(e, 'Failed to load organization');
      } finally {
        loading.value = false;
      }
    });

    return {
      org,
      members,
      invitations,
      loading,
      membersLoading,
      error,
      memberHeaders,
      showInviteModal,
      showUpdateRolesModal,
      showRemoveModal,
      showTransferModal,
      selectedMember,
      inviteEmail,
      inviteRoles,
      updatedRoles,
      inviteLoading,
      rolesLoading,
      removeLoading,
      transferLoading,
      revokingId,
      closeView,
      formatDate,
      sendInvitation,
      revokeInvitation,
      openUpdateRoles,
      submitUpdateRoles,
      confirmRemoveMember,
      submitRemoveMember,
      openTransferOwnership,
      submitTransferOwnership,
    };
  },
};
</script>
